import { isPlatformBrowser } from '@angular/common';
import { inject, Injectable, PLATFORM_ID, signal } from '@angular/core';
import { finalize } from 'rxjs';
import { ApiService } from './api.service';
import { CartStore } from './cart.store';
import { LocationSelection, Serviceability, ServiceLocation } from './models';

@Injectable({ providedIn: 'root' })
export class LocationStore {
  private readonly api = inject(ApiService);
  private readonly cart = inject(CartStore);
  private readonly platformId = inject(PLATFORM_ID);
  readonly selection = signal<LocationSelection | null>(this.restore());
  readonly options = signal<ServiceLocation[]>([]);
  readonly panelOpen = signal(false);
  readonly checking = signal(false);
  readonly error = signal('');
  readonly revision = signal(0);

  constructor() {
    const selection = this.selection();
    if (selection) this.syncCart(selection);
  }

  checkPostal(postalCode: string) {
    const normalized = postalCode.trim();
    if (!normalized) { this.error.set('Enter a postal code.'); return; }
    this.checking.set(true); this.error.set('');
    this.api.get<Serviceability>('/serviceability', { postal_code: normalized }).pipe(finalize(() => this.checking.set(false))).subscribe({
      next: response => {
        if (!response.data.purchase_allowed || !response.data.service_location) {
          this.error.set('Sorry, Grih Upvan is not delivering to this location yet.'); return;
        }
        this.apply({ postal_code: response.data.postal_code, service_location: response.data.service_location });
      },
      error: error => this.error.set(error?.error?.message ?? 'Location could not be checked.'),
    });
  }

  selectLocation(location: ServiceLocation) {
    this.apply({ postal_code: null, service_location: location });
  }

  open() { this.panelOpen.set(true); this.loadOptions(); }
  toggle() { this.panelOpen.update(value => !value); if (this.panelOpen()) this.loadOptions(); }

  private loadOptions() {
    if (this.options().length) return;
    this.api.get<ServiceLocation[]>('/locations').subscribe({ next: response => this.options.set(response.data) });
  }

  private apply(selection: LocationSelection) {
    this.selection.set(selection); this.api.rememberLocation(selection); this.panelOpen.set(false); this.revision.update(value => value + 1);
    this.syncCart(selection);
  }

  private syncCart(selection: LocationSelection) {
    if (!selection.postal_code) {
      this.api.delete<any>('/cart/location').subscribe({ next: response => this.cart.accept(response.data), error: () => undefined });
      return;
    }
    this.api.post<any>('/cart/location', { postal_code: selection.postal_code }).subscribe({ next: response => this.cart.accept(response.data), error: () => undefined });
  }

  private restore(): LocationSelection | null {
    if (!isPlatformBrowser(this.platformId)) return null;
    try { return JSON.parse(localStorage.getItem('greenlife_location') ?? 'null'); } catch { return null; }
  }
}
